import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles, MessageSquare, Bot, Plus, PanelLeftClose, PanelLeft,
  Moon, Sun, Globe, Home, Wand2, Crown, LogOut, Shield, Images
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ConversationList } from './ConversationList';
import { Button } from './ui/button';

const AppSidebar = ({ conversations = [], onNewChat, onSelectConv, onDeleteConv, activeConvId, onConversationsChange, showHistory = false }) => {
  const { t, theme, toggleTheme, lang, toggleLang } = useApp();
  const { user, logout } = useAuth();
  const isPro = user?.plan === 'pro';
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { label: t.nav.chat, to: '/chat', icon: MessageSquare },
    { label: t.nav.agents, to: '/agents', icon: Bot },
    { label: t.nav.build, to: '/build', icon: Wand2 },
    { label: t.nav.gallery, to: '/gallery', icon: Images },
    ...(user?.role === 'admin' ? [{ label: t.admin.title, to: '/admin', icon: Shield }] : [])
  ];

  return (
    <aside className={`${collapsed ? 'w-[68px]' : 'w-64'} hidden md:flex shrink-0 flex-col border-r border-border bg-secondary/30 transition-all duration-300 h-screen sticky top-0`}>
      <div className="flex items-center justify-between h-16 px-3 border-b border-border">
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2 pl-1">
            <span className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </span>
            <span className="font-display text-lg font-bold">Aurra<span className="text-primary">.</span></span>
          </Link>
        )}
        <button onClick={() => setCollapsed((c) => !c)} className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground">
          {collapsed ? <PanelLeft className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
        </button>
      </div>

      <div className="p-3">
        <Button onClick={onNewChat} className="w-full rounded-xl font-semibold justify-center gap-2">
          <Plus className="h-4 w-4" /> {!collapsed && t.chat.newChat}
        </Button>
      </div>

      <nav className="px-3 space-y-1">
        {navItems.map((n) => {
          const active = location.pathname.startsWith(n.to);
          return (
            <button data-testid={`nav-${n.to.slice(1)}`} key={n.to} onClick={() => navigate(n.to)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
              <n.icon className="h-[18px] w-[18px] shrink-0" /> {!collapsed && n.label}
            </button>
          );
        })}
      </nav>

      {!collapsed && showHistory && (
        <ConversationList conversations={conversations} activeConvId={activeConvId} onSelectConv={onSelectConv} onDeleteConv={onDeleteConv} onConversationsChange={onConversationsChange} />
      )}

      <div className={`mt-auto border-t border-border`}>
        <button onClick={() => navigate('/account')} data-testid="sidebar-account-btn" className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-accent transition-colors ${collapsed ? 'justify-center' : ''}`}>
          {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-lg object-cover shrink-0" /> : <span className="h-8 w-8 shrink-0 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">{user?.name?.[0]?.toUpperCase() || 'A'}</span>}
          {!collapsed && (
            <span className="min-w-0 flex-1 text-left">
              <span className="block text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</span>
              <span className={`inline-block mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${isPro ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`} data-testid="sidebar-plan-badge">{isPro ? 'PRO' : t.account.free}</span>
            </span>
          )}
        </button>
        {!isPro && !collapsed && (
          <div className="px-3 pb-2">
            <Button onClick={() => navigate('/upgrade')} size="sm" variant="outline" data-testid="sidebar-upgrade-btn" className="w-full rounded-lg font-semibold gap-1.5 border-primary/40 text-primary hover:bg-primary/10 hover:text-primary">
              <Crown className="h-3.5 w-3.5" /> {t.account.upgrade}
            </Button>
          </div>
        )}
        <div className={`p-3 pt-1 flex ${collapsed ? 'flex-col' : ''} items-center gap-1`}>
          <button onClick={() => navigate('/')} className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground" title="Home">
            <Home className="h-[18px] w-[18px]" />
          </button>
          <button onClick={toggleTheme} data-testid="sidebar-theme-toggle" className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground">
            {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <button onClick={toggleLang} data-testid="sidebar-lang-toggle" className="h-9 px-2 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground text-sm font-semibold gap-1">
            <Globe className="h-4 w-4" /> {!collapsed && lang.toUpperCase()}
          </button>
          <button onClick={() => { navigate('/'); logout(); }} data-testid="sidebar-logout-btn" title={t.account.logout} className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground ml-auto">
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default AppSidebar;
